import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin, Clock, ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';

// Image + price metadata stays here, all text translated via i18n
const DESTINATIONS = [
  {
    id: 'braga',
    price: '€120',
    image: 'https://images.unsplash.com/photo-1630518690256-47d9a78d22c6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwyfHxwb3J0dWdhbCUyMHRvdXJpc218ZW58MHx8fHwxNzc1ODA1OTExfDA&ixlib=rb-4.1.0&q=85',
    highlightKeys: ['h1', 'h2', 'h3']
  },
  {
    id: 'guimaraes',
    price: '€110',
    image: 'https://images.unsplash.com/photo-1625668349843-1781791b0ae1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwyfHxndWltYXJhZXMlMjBjYXN0bGUlMjBwb3J0dWdhbCUyMG1lZGlldmFsfGVufDB8fHx8MTc3NTg0Mjg2Mnww&ixlib=rb-4.1.0&q=85',
    highlightKeys: ['h1', 'h2', 'h3']
  },
  {
    id: 'santiago',
    price: '€250',
    image: 'https://images.unsplash.com/photo-1638704645810-708030b50ce5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwxfHxzYW50aWFnbyUyMGNvbXBvc3RlbGElMjBjYXRoZWRyYWwlMjBzcGFpbiUyMGdhbGljaWF8ZW58MHx8fHwxNzc1ODQzMDgyfDA&ixlib=rb-4.1.0&q=85',
    highlightKeys: ['h1', 'h2', 'h3']
  },
  {
    id: 'aveiro',
    price: '€140',
    image: 'https://images.unsplash.com/photo-1670613710626-55b6f7a3c42a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwxfHxhdmVpcm8lMjBjYW5hbHMlMjBtb2xpY2Vpcm9zJTIwYm9hdHMlMjBwb3J0dWdhbHxlbnwwfHx8fDE3NzU4NDI4NjJ8MA&ixlib=rb-4.1.0&q=85',
    highlightKeys: ['h1', 'h2', 'h3']
  },
  {
    id: 'fatima',
    price: '€150',
    image: 'https://images.unsplash.com/photo-1644953443604-ecff0776fd39?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwyfHxmYXRpbWElMjBzYW5jdHVhcnklMjBwb3J0dWdhbCUyMGJhc2lsaWNhfGVufDB8fHx8MTc3NTgxMTc0N3ww&ixlib=rb-4.1.0&q=85',
    highlightKeys: ['h1', 'h2', 'h3']
  },
  {
    id: 'nazare',
    price: '€160',
    image: 'https://images.unsplash.com/photo-1738316849650-fcb05a668069?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwxfHxuYXphcmUlMjBnaWFudCUyMHdhdmUlMjBwb3J0dWdhbCUyMHN1cmZpbmd8ZW58MHx8fHwxNzc1ODExNzQ3fDA&ixlib=rb-4.1.0&q=85',
    highlightKeys: ['h1', 'h2', 'h3']
  },
  {
    id: 'douro',
    price: '€280',
    image: 'https://images.unsplash.com/photo-1638664370752-8188076afbab?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwxfHxkb3VybyUyMHZhbGxleSUyMHZpbmV5YXJkcyUyMHJpdmVyJTIwcG9ydHVnYWx8ZW58MHx8fHwxNzc1ODQyODYyfDA&ixlib=rb-4.1.0&q=85',
    highlightKeys: ['h1', 'h2', 'h3', 'h4']
  },
  {
    id: 'paiva',
    price: '€180',
    image: 'https://images.unsplash.com/photo-1685709637328-2043d120ef3d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwxfHxwYXNzYWRpJUMzJUE3b3MlMjBkbyUyMHBhaXZhJTIwc3VzcGVuZGVkJTIwYnJpZGdlJTIwcG9ydHVnYWx8ZW58MHx8fHwxNzc2NDE4Mjk4fDA&ixlib=rb-4.1.0&q=85',
    highlightKeys: ['h1', 'h2', 'h3', 'h4']
  }
];

export const Destinations = () => {
  const { t } = useTranslation();
  const [expandedCard, setExpandedCard] = useState(null);

  const toggleCard = (id) => {
    setExpandedCard(expandedCard === id ? null : id);
  };

  return (
    <section id="destinations" className="py-24 bg-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" data-testid="destinations-title">
            {t('destinations.title')}
          </h2>
          <p className="text-xl text-gray-600">
            {t('destinations.subtitle')}
          </p>
        </div>

        {/* Destinations Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {DESTINATIONS.map((destination) => (
            <Card 
              key={destination.id} 
              className="group overflow-hidden hover:shadow-2xl transition-all duration-300 border-0"
              data-testid={`destination-card-${destination.id}`}
            >
              {/* Destination Image */}
              <div className="relative h-56 overflow-hidden">
                <img
                  src={destination.image}
                  alt={t(`destinations.items.${destination.id}.name`)}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                
                {/* Price Badge */}
                <div className="absolute top-4 right-4 bg-amber-600 text-white px-4 py-2 rounded-full font-bold shadow-lg">
                  {t('destinations.starting_from')} {destination.price}
                </div>

                {/* Destination Name */}
                <div className="absolute bottom-4 left-4 right-4">
                  <h3 className="text-2xl font-bold text-white mb-1">
                    {t(`destinations.items.${destination.id}.name`)}
                  </h3>
                  <div className="flex items-center text-white/90 text-sm">
                    <Clock className="w-4 h-4 mr-1" />
                    {t(`destinations.items.${destination.id}.duration`)}
                  </div>
                </div>
              </div>

              <CardContent className="p-6">
                {/* Description */}
                <p className={`text-gray-600 mb-4 leading-relaxed ${expandedCard === destination.id ? '' : 'line-clamp-2'}`}>
                  {t(`destinations.items.${destination.id}.description`)}
                </p>

                {/* Highlights */}
                {expandedCard === destination.id && (
                  <div className="mb-4 space-y-2 animate-in fade-in duration-300">
                    <h4 className="font-semibold text-gray-900 text-sm uppercase tracking-wide">
                      {t('destinations.highlights')}
                    </h4>
                    {destination.highlightKeys.map((key, index) => (
                      <div key={index} className="flex items-start space-x-2">
                        <MapPin className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700 text-sm">
                          {t(`destinations.items.${destination.id}.${key}`)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Toggle Button */}
                <Button
                  variant="ghost"
                  className="w-full text-amber-700 hover:text-amber-800 hover:bg-amber-50"
                  onClick={() => toggleCard(destination.id)}
                  data-testid={`destination-toggle-${destination.id}`}
                >
                  {expandedCard === destination.id ? (
                    <>
                      {t('destinations.see_less')}
                      <ChevronUp className="w-4 h-4 ml-2" />
                    </>
                  ) : (
                    <>
                      {t('destinations.see_more')}
                      <ChevronDown className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <p className="text-gray-600 mb-4">
            {t('destinations.cta_text')}
          </p>
          <a href="https://wa.me/351932651940" target="_blank" rel="noopener noreferrer">
            <Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="destinations-cta-btn">
              {t('destinations.cta_button')}
            </Button>
          </a>
        </div>
      </div>
    </section>
  );
};
